<template>
	<b-container class='report-page'>
            <b-row>
                <b-col>
                    <b-form-group>
                        <label>{{ $t('punctuality_report.block') }}</label>
                        <vSelect :options="blockOptions" v-model="selectedBlock" />
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group>
                        <label>{{ $t('punctuality_report.line') }}</label>
                        <!-- vSelect :options="lineOptions" v-model="selectedLine" label="tunnusteksti" :reduce="line => line.tunnus" / -->
                        <vSelect :options="lineOptions" v-model="selectedLine" />
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group>
                        <label>{{ $t('report.start_date') }}</label>
                        <b-form-datepicker 
                            :placeholder="this.$t('datepicker.labelNoDateSelected')"
                            v-model="startDate" 
                            :locale="this.$t('datepicker.locale')" 
                            :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'short' }" >
                        </b-form-datepicker>
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group>
                        <label>{{ $t('report.end_date') }}</label>
                        <b-form-datepicker 
                            :placeholder="this.$t('datepicker.labelNoDateSelected')"
                            v-model="endDate" 
                            :locale="this.$t('datepicker.locale')" 
                            :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'short' }" >
                        </b-form-datepicker>
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group>
                        <label>{{ $t('punctuality_report.min_early') }}</label>
                        <vSelect :options="timeOptions" v-model="minEarly" :reduce="option => option.minutes" />
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group>
                        <label>{{ $t('punctuality_report.min_late') }}</label>
                        <vSelect :options="timeOptions" v-model="minLate" :reduce="option => option.minutes" />
                    </b-form-group>
                </b-col>
            </b-row>
            <b-row class='btn-row'>
                <b-col>
                    <b-button class="btn-success btn-report" @click="generateReport()" :disabled="buttonDisabled">
                        <b-icon icon="arrow-clockwise" scale="1.2"></b-icon>
                        {{ $t('report.generate') }}
                    </b-button>
                    <b-button class="btn-success btn-report" @click="printReport()" :disabled="buttonDisabled || !generated">
                        <b-icon icon="printer" scale="1.2"></b-icon>
                        {{ $t('report.print') }}
                    </b-button>
                    <b-button class="btn-success btn-report" @click="generateXLS()" :disabled="buttonDisabled || !generated">
                        <b-icon icon="download" scale="1.2"></b-icon>
                        {{ $t('report.download') }}
                    </b-button>
                </b-col>  
            </b-row>
			<b-row>
				<b-col>
                    <b-table
						striped
						hover
						:items="items"
						:fields="fields"
						:per-page="perPage"
						:current-page="currentPage"
                        :busy="loading">

                    <template #table-busy>
                        <Loading />
                    </template>
					
                    <template v-slot:cell(deviation)="row">
                        {{ getDeviation(row.item.departureTime, row.item.realTime) }}
                    </template>

                    <template v-slot:cell(actions)="row">
                        <b-button size="sm" @click="row.toggleDetails">{{ row.detailsShowing ? '-' : '+' }}</b-button>
                    </template>

                    <template v-slot:row-details="row">
                        <b-card>
                            <!-- b-table :items="row.item.passings" :fields="stopListFields">
                                <template v-slot:cell(deviation)="row">
                                    {{ getDeviation(row.item.passingTime, row.item.realTime) }}
                                </template>
                            </b-table -->

                            <b-container>
                                <b-row>
                                    <b-col>{{ $t('punctuality_report.order') }}</b-col>
                                    <b-col>{{ $t('punctuality_report.stop') }}</b-col>
                                    <b-col>{{ $t('punctuality_report.scheduled_time') }}</b-col>
                                    <b-col>{{ $t('punctuality_report.real_time') }}</b-col>
                                    <b-col>{{ $t('punctuality_report.deviation') }}</b-col>
                                </b-row>
                                <b-row v-for="passing in row.item.passings" :key="passing.stop+'_'+passing.realTime"
                                    style="border-top: 1px solid; padding-top: 3px">
                                    <b-col>{{ passing.stopOrder }}</b-col>
                                    <b-col>{{ passing.stopName ? passing.stop +' '+ passing.stopName : passing.stop }}</b-col>
                                    <b-col>{{ formatTime(passing.passingTime, 'DD.MM.YYYY HH:mm') }}</b-col>
                                    <b-col>{{ formatTime(passing.realTime, 'HH:mm:ss') }}</b-col>
                                    <b-col>{{ getDeviation(passing.passingTime, passing.realTime) }}</b-col>
                                </b-row>
                            </b-container>
                        </b-card>
                    </template>
					</b-table>

					<TablePagination
						:total-rows="totalRows"
						:per-page="perPage"
						@pageChange="onPageChange"
					>
					</TablePagination>
				</b-col>
			</b-row>
	</b-container>
</template>

<script>
import TablePagination from '@/components/Misc/TablePagination';
import punctualityReport from '@/store/realtime/punctualityReport';
import vSelect from 'vue-select'
import moment from 'moment';

export default {
	components: { TablePagination, vSelect },
	data: function() {
		return {
			perPage: 30,
			currentPage: 1,
			filteredRows: 0,
            availableLines: [],
            selectedBlock: null,
            selectedLine: null,
            startDate: new moment().format('YYYY-MM-DD'),
			endDate: new moment().format('YYYY-MM-DD'),
            minEarly: 3,
            minLate: 3,
            loading: false,
            generated: false,
            printing : false
		};
	},
	computed: {
		fields() {
            return [
                //{ key: 'block', label: this.$t('punctuality_report.block'), sortable: true },
				{ key: 'line', label: this.$t('punctuality_report.line'), sortable: true },
				{ key: 'departureTime', label: this.$t('punctuality_report.departure_time'), sortable: true,
                    formatter: function(value) {
                        var time = new moment(value, 'YYYY-MM-DD[T]HH:mm:ssZZ').format('DD.MM.YYYY HH:mm');
                        if (time.startsWith('Invalid')) {
                            return '';
                        }
                        return time;
                    }
                },
				{ key: 'realTime', label: this.$t('punctuality_report.real_time'), sortable: true,
                    formatter: function(value) {
                        var time = new moment(value, 'YYYY-MM-DD[T]HH:mm:ssZZ').format('HH:mm:ss');
                        if (time.startsWith('Invalid')) {
                            return '';
                        }
                        return time;
                    }
                },
				{ key: 'deviation', label: this.$t('punctuality_report.deviation'), sortable: true },
                { key: 'actions', label: '' }
            ];
        },
		items() {
			return this.$store.state.punctualityReport.punctualityReportList;
		},
        blockOptions() {
            return this.$store.state.punctualityReport.availableBlocks;
        },
        lineOptions() {
            return this.availableLines;
        },
        timeOptions() {
            var options = [];
            for (var i = 0; i < 11; i++) {
                options.push({ 'minutes': i, 'label': i + ' min.' });
            }
            return options;
        },
        buttonDisabled() {
			return !this.selectedLine || !this.startDate || !this.endDate 
					|| (moment(this.endDate, "YYYY-MM-DD") > moment(this.startDate, "YYYY-MM-DD").add(7, 'days') || this.startDate > this.endDate)
					|| this.loading || this.printing;
        },
        stopListFields() {
             return [
				{ key: 'stop', label: this.$t('punctuality_report.stop'), sortable: false },
				{ key: 'passingTime', label: this.$t('punctuality_report.scheduled_time'), sortable: false,
                    formatter: function(value) {
                        var time = new moment(value, 'YYYY-MM-DD[T]HH:mm:ssZZ').format('HH:mm');
                        if (time.startsWith('Invalid')) {
                            return '';
                        }
                        return time;
                    }
                },
				{ key: 'realTime', label: this.$t('punctuality_report.real_time'), sortable: true,
                    formatter: function(value) {
                        var time = new moment(value, 'YYYY-MM-DD[T]HH:mm:ssZZ').format('HH:mm:ss');
                        if (time.startsWith('Invalid')) {
                            return '';
                        }
                        return time;
                    }
                },
				{ key: 'deviation', label: this.$t('punctuality_report.deviation'), sortable: true },
                { key: 'actions', label: '' }
            ];
        },
		totalRows() {
			return this.filteredRows ? this.filteredRows : (this.items ? this.items.length : 0);
		},
	},
    watch: {
        selectedBlock() {
            if (this.selectedBlock) {
                //this.availableLines = this.$store.state.punctualityReport.availableLinesByBlock[this.selectedBlock];
                this.availableLines = this.$store.state.punctualityReport.availableLineNumbersByBlock[this.selectedBlock];
            } else {
                //this.availableLines = this.$store.state.punctualityReport.availableLines;
                this.availableLines = this.$store.state.punctualityReport.availableLineNumbers;
            }
        }
    },
    beforeCreate() {
		if (!this.$store.state.punctualityReport) {
            this.$store.registerModule("punctualityReport", punctualityReport);
        }
        this.$store.dispatch('punctualityReport/getAvailableBlocks');
        //this.$store.dispatch('punctualityReport/getAvailableLines');
        //this.$store.dispatch('punctualityReport/getAvailableLinesByBlock');
        this.$store.dispatch('punctualityReport/getAvailableLineNumbers');
        this.$store.dispatch('punctualityReport/getAvailableLineNumbersByBlock');
	},
    created() {
        //this.availableLines = this.$store.state.punctualityReport.availableLines;
        this.availableLines = this.$store.state.punctualityReport.availableLineNumbers;

        window.addEventListener("afterprint", this.afterprint);
    },
    beforeDestroy(){
        window.removeEventListener("afterprint", this.afterprint);
    },
	methods: {
		onFiltered(filteredItems) {
			this.filteredRows = filteredItems.length;
			this.currentPage = 1;
		},
		filterFunction(itemRow) {
			if (this.selectedLine != null && this.selectedLine.length > 0 && (itemRow.line == null || !itemRow.line.toLowerCase().includes(this.selectedLine.toLowerCase()))) {
				return false;
			}
			return true;
		},
		onPageChange(page) {
			this.currentPage = page;
		},
        generateReport() {
            this.loading = true;

            var startTime = new moment(this.startDate, 'YYYY-MM-DD');
            var endTime = new moment(this.endDate, 'YYYY-MM-DD');
            var params = {
                'line': this.selectedLine,
                'startTime': startTime.format('YYYY-MM-DD[T]HH:mm:ss'),
                'endTime': endTime.format('YYYY-MM-DD[T]23:[59:59]'),
                'minEarly': this.minEarly,
                'minLate': this.minLate
            }
            this.$store.dispatch('punctualityReport/getPunctualityReportList', params)
                .finally(() => { this.loading = false; this.generated = true });
        },
        generateXLS() {
            this.loading = true;

            var startTime = new moment(this.startDate, 'YYYY-MM-DD');
            var endTime = new moment(this.endDate, 'YYYY-MM-DD');
            var params = {
                'line': this.selectedLine,
                'startTime': startTime.format('YYYY-MM-DD[T]HH:mm:ss'),
                'endTime': endTime.format('YYYY-MM-DD[T]23:[59:59]'),
                'minEarly': this.minEarly,
                'minLate': this.minLate,
                'header1': this.$t('punctuality_report.line'),
                'header2': this.$t('punctuality_report.departure_time'),
                'header3': this.$t('punctuality_report.order'),
                'header4': this.$t('punctuality_report.stop'),
                'header5': this.$t('punctuality_report.scheduled_time'),
                'header6': this.$t('punctuality_report.real_time'),
                'header7': this.$t('punctuality_report.deviation')
            }
            this.$store.dispatch('punctualityReport/exportXLS', params)
                .finally(() => { this.loading = false; });
        },
        getDeviation(scheduledTime, realTime) {
            if (!scheduledTime || !realTime) {
                return '';
            }
            var scheduled = new moment(scheduledTime, 'YYYY-MM-DD[T]HH:mm:ssZZ');
            var real = new moment(realTime, 'YYYY-MM-DD[T]HH:mm:ssZZ');
            var deviation = new moment.duration(real.diff(scheduled)).asMilliseconds();
            if (deviation >= 0) {
                return new moment.utc(deviation).format('[+]HH:mm:ss');
            }
            return new moment.utc(new moment.duration(scheduled.diff(real)).asMilliseconds()).format('[-]HH:mm:ss');
        },
        formatTime(time, format) {
            var formatted = new moment(time, 'YYYY-MM-DD[T]HH:mm:ssZZ').format(format);
            if (formatted.startsWith('Invalid')) {
                return '';
            }
            return formatted;
        },
        printReport(){
            this.printing = true;
            this.perPage = this.items.length
            
            // timeout needed because table takes some time to render
            setTimeout(() => { window.print(); }, 10);   
        },
        afterprint(){
            this.perPage = 30;
            this.printing = false;
        }
	}
};
</script>